import {useEffect} from 'react';

import {
  GetDatasetColumns,
  GetDatasetColumns_dataset_DatasetNode as DatasetColumnsDataset,
  GetDatasetColumns_dataset_DatasetNode_dataFrame_DataFrameNode as DatasetColumnsDataFrame,
  GetDatasetColumnsVariables,
} from 'data-frames/graphql/__generated/GetDatasetColumns';
import {GET_DATASET_COLUMNS_QUERY} from 'data-frames/graphql/GetDataFrame';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';

const POLL_INTERVAL_MS = 1000;
const GENERIC_FAILURE_MESSAGE = 'Dataset processing failed.';
const INACCESSIBLE_FAILURE_MESSAGE = "You don't have access to this dataset.";

type PollResult = SkippableBenchlingQueryResult<GetDatasetColumns, GetDatasetColumnsVariables>;

export interface PollForDatasetProcessingToCompleteResult {
  datasetId: string | null;
  queryStatus: QueryResultType;
  dataset: DatasetColumnsDataset | null;
  dataFrame: DatasetColumnsDataFrame | null;
}

/**
 * Polls GET_DATASET_COLUMNS_QUERY until the dataset finishes processing or an error occurs.
 */
export function usePollForDatasetProcessingToComplete(
  datasetId: string | null
): PollForDatasetProcessingToCompleteResult {
  const pollResult = useSkippableBenchlingQuery<GetDatasetColumns, GetDatasetColumnsVariables>(
    GET_DATASET_COLUMNS_QUERY,
    {
      variables: {datasetId: datasetId!},
      fetchPolicy: 'network-only',
      skip: datasetId == null,
      pollInterval: POLL_INTERVAL_MS,
    }
  );

  const dataset = getDatasetFromQueryResponse(pollResult);
  const dataFrame = getDataFrameFromQueryResponse(dataset);

  // Once the data frame's upload status leaves IN_PROGRESS (or an error occurs), stop polling.
  const isProcessingComplete =
    pollResult.type === QueryResultType.ERROR ||
    (pollResult.type === QueryResultType.SUCCESS &&
      (dataFrame == null || dataFrame.uploadStatus !== 'IN_PROGRESS'));

  const {stopPolling} = pollResult;
  useEffect(() => {
    if (isProcessingComplete) {
      stopPolling();
    }
  }, [isProcessingComplete, stopPolling]);

  // Notify the user of any error. Once isProcessingComplete stops polling, notifyMessage stops changing,
  // so this won't re-notify on every render.
  const notifyMessage = getUserFacingErrorMessage(pollResult, dataset, dataFrame);
  useEffect(() => {
    if (notifyMessage != null) {
      notifications.error(notifyMessage);
    }
  }, [notifyMessage]);

  return {datasetId, queryStatus: pollResult.type, dataset, dataFrame};
}

function getDatasetFromQueryResponse(pollResult: PollResult): DatasetColumnsDataset | null {
  if (pollResult.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const {dataset} = pollResult.data;
  return dataset.__typename === 'DatasetNode' ? dataset : null;
}

function getDataFrameFromQueryResponse(
  dataset: DatasetColumnsDataset | null
): DatasetColumnsDataFrame | null {
  return dataset?.dataFrame.__typename === 'DataFrameNode' ? dataset.dataFrame : null;
}

/** Get error message to show the user, if uploading fails. */
function getUserFacingErrorMessage(
  pollResult: PollResult,
  dataset: DatasetColumnsDataset | null,
  dataFrame: DatasetColumnsDataFrame | null
): string | null {
  // Query errors are already shown to the user by `useSkippableBenchlingQuery`
  if (pollResult.type !== QueryResultType.SUCCESS) {
    return null;
  }
  if (dataset == null || dataFrame == null) {
    return INACCESSIBLE_FAILURE_MESSAGE;
  }
  if (dataFrame.uploadStatus === 'FAILED_VALIDATION') {
    return dataFrame.errorMessage ?? GENERIC_FAILURE_MESSAGE;
  }
  return null;
}
